import {
  buildCategoryContinua,
  CONTINUUM_METRICS,
  type CategoryContinuum
} from '../../lib/acoustic/calibrationCategoryContinuum';
import type { AcousticCalibrationSnapshot } from '../../lib/acoustic/calibrationTypes';

type CategoryContinuumTableProps = {
  snapshots: AcousticCalibrationSnapshot[];
  highlightTag?: string;
};

export default function CategoryContinuumTable({ snapshots, highlightTag }: CategoryContinuumTableProps) {
  const continua = buildCategoryContinua(snapshots);
  if (!continua.length) {
    return (
      <p className="text-xs text-gray-500">Noch keine getaggten Snapshots für Kategorie-Kontinuua.</p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-600 dark:text-gray-400">
        Pro Kategorie: erfasstes Spektrum (min · Schwerpunkt · max) je Kennwert. Schwellen werden so gesetzt,
        dass das Ziel-Kontinuum abgedeckt und von anderen Kategorien getrennt wird.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-xs">
          <thead>
            <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700">
              <th className="px-2 py-1.5">Kategorie</th>
              <th className="px-2 py-1.5">Kennwert</th>
              <th className="px-2 py-1.5 text-right">Min</th>
              <th className="px-2 py-1.5 text-right">Schwerpunkt</th>
              <th className="px-2 py-1.5 text-right">Max</th>
              <th className="px-2 py-1.5 text-right">n</th>
            </tr>
          </thead>
          <tbody>
            {continua.map((continuum) => (
              <CategoryRows
                key={continuum.tag}
                continuum={continuum}
                highlighted={continuum.tag === highlightTag}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoryRows({ continuum, highlighted }: { continuum: CategoryContinuum; highlighted: boolean }) {
  const rows = CONTINUUM_METRICS.filter((m) => continuum.metrics[m.key]).map((m, index) => {
    const stats = continuum.metrics[m.key]!;
    return (
      <tr
        key={`${continuum.tag}-${m.key}`}
        className={`border-b border-gray-100 dark:border-gray-800 ${
          highlighted ? 'bg-sky-50/80 dark:bg-sky-950/30' : ''
        }`}
      >
        <td className="px-2 py-1 text-gray-700 dark:text-gray-300">
          {index === 0 ? (
            <span className="font-semibold">
              {continuum.tagLabel}
              <span className="ml-1 font-normal text-gray-500">({continuum.snapshotCount})</span>
            </span>
          ) : null}
        </td>
        <td className="px-2 py-1 text-gray-600 dark:text-gray-400">{m.label}</td>
        <td className="px-2 py-1 text-right font-mono">{stats.min.toFixed(3)}</td>
        <td className="px-2 py-1 text-right font-mono text-sky-700 dark:text-sky-300">
          {stats.mean.toFixed(3)}
        </td>
        <td className="px-2 py-1 text-right font-mono">{stats.max.toFixed(3)}</td>
        <td className="px-2 py-1 text-right text-gray-500">{stats.count}</td>
      </tr>
    );
  });

  return <>{rows}</>;
}
